import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';

import { Container, Content } from 'styles/sgo_wrappers';
import { useHistory, useLocation } from 'react-router-dom';

import SGOHeader from 'components/SGOHeader';
import SGONavbar from 'components/SGONavbar';
import ScrollTop from 'components/ScrollTop';
import SGOFooter from 'components/SGOFooter';
import Loading from 'components/Loading';

import api from 'services/api';
import { UpdateProps } from 'styles/dialog_update';
import * as U from 'styles/dialog_update';
import { IncludeProps } from 'styles/dialog_include';
import * as I from 'styles/dialog_include';

import { useCredentials } from 'hooks/credentials';
import { useAuth } from 'hooks/auth';
import { useToast } from 'hooks/toast';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';

import { formatDate } from 'utils/formatDate';
import { calcTotal, formatNumber } from 'utils/calcTotal';
import Checkbox from 'components/Checkbox';
import { useSpring } from 'react-spring';
import { FaAngleDoubleRight } from 'react-icons/fa';
import { ListProps } from '../../Pedido/main';

import {
  HeaderContent,
  Total,
  Grid,
  GridItem,
  SpanMessage,
  DeclareButton,
  DeclareNumbers,
  ButtonFinish,
  Insufficient,
  FaultyDiv,
  OBS,
} from './styles';

interface StateProps {
  pedidoHeader: ListProps;
}

interface ItemsProps {
  matcod: string;
  matdesc: string;
  cost: number;
  quantity: number;
  stored: number;
  require: boolean;
  numbers: string[];
  selected: string[];
}

const GDPedido: React.FC = () => {
  const phaseOne = useRef<FormHandles>(null);
  const location = useLocation<StateProps>();
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();
  const { errorHandling } = useCredentials();
  const [loading, setLoading] = useState(true);
  const [pedidoHeader] = useState<ListProps>(() => {
    if (!location.state) {
      return {} as ListProps;
    }
    return location.state.pedidoHeader;
  });

  const [inc, setInc] = useState<IncludeProps>({
    title: 'Observação da GD',
    open: false,
    content: (
      <OBS>
        <textarea id="obs" placeholder="Insira a observação (opcional)" />
      </OBS>
    ),
  });

  const [items, setItems] = useState<ItemsProps[]>([]);

  const [updateDiag, setUpdateDiag] = useState<UpdateProps>({
    open: false,
    content: '',
    values: {},
  });

  const getItems = useCallback(async () => {
    try {
      const response = await api.get(
        `/sgo/getStorage.php?data=${JSON.stringify({
          trans: 'gdpedido',
          pednum: pedidoHeader.num,
          anbcod: pedidoHeader.cod,
        })}`,
      );

      setItems(response.data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      errorHandling(err);
    }
  }, [errorHandling, pedidoHeader.cod, pedidoHeader.num]);

  useEffect(() => {
    getItems();
  }, [getItems]);

  const total = useMemo(() => {
    let stotal = 0;

    items.forEach((item: ItemsProps) => {
      stotal += item.quantity * item.cost;
    });

    return stotal;
  }, [items]);

  const handleDeclareClick = useCallback(
    (matcod: string, number: string) => {
      const index = items.findIndex(
        (item: ItemsProps) => item.matcod === matcod,
      );

      const exists = [...items[index].selected].includes(number);

      items[index].selected = exists
        ? [...items[index].selected].filter((item: string) => item !== number)
        : [...items[index].selected, number];

      setItems((state) => [...state]);
    },
    [items],
  );

  const faulty = useMemo(() => {
    return items.reduce(
      (accum, { quantity, selected, require, stored }: ItemsProps) => {
        return require
          ? quantity === selected.length
            ? accum
            : accum + 1
          : stored < quantity
          ? accum + 1
          : accum;
      },
      0,
    );
  }, [items]);

  const appear = useSpring({
    opacity: !loading && faulty === 0 ? 1 : 0,
    pointerEvents: !loading && faulty === 0 ? 'all' : 'none',
    transform:
      !loading && faulty === 0 ? 'translateX(5px)' : 'translateX(60px)',
    height: !loading && faulty === 0 ? '35px' : '0px',
    overflow: !loading && faulty === 0 ? 'visible' : 'hidden',
  });

  const faultyAppear = useSpring({
    opacity: !loading && faulty > 0 ? 1 : 0,
    pointerEvents: !loading && faulty > 0 ? 'all' : 'none',
    height: !loading && faulty > 0 ? '30px' : '0px',
  });

  const handleFinish = useCallback(async () => {
    try {
      setLoading(true);
      setInc((state) => ({ ...state, open: !state }));

      let obs = '*** SEM OBSERVAÇÃO ***';
      const retrieve = document.querySelector<HTMLTextAreaElement>(
        'textarea[id="obs"]',
      );

      if (retrieve && retrieve.value.trim() !== '') {
        obs = retrieve.value;
      }

      const data = {
        dest: {
          cod: pedidoHeader.cod,
          desc: pedidoHeader.anbdesc,
        },
        items,
        address: {
          value: pedidoHeader.addressval,
          label: pedidoHeader.address.street,
        },
        obs,
        name: user.name,
        zondesc: user.zondesc,
        total,
        from: 'pedido',
        pednum: pedidoHeader.num,
      };

      const send = new FormData();
      send.append('data', JSON.stringify(data));

      const response = await api.post('/sgo/gd_insert.php', send, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { gdnum } = response.data;
      addToast({
        type: 'success',
        title: 'Sucesso!',
        description: `Guia de Distribuição N° ${gdnum} emitida para a Coordenação ${pedidoHeader.anbdesc}.`,
      });

      history.replace(location.pathname.replace('gd/pedido', 'pedido'));
      setLoading(false);
    } catch (err) {
      setLoading(false);
      errorHandling(err);
    }
  }, [
    addToast,
    errorHandling,
    history,
    items,
    location.pathname,
    pedidoHeader.address.street,
    pedidoHeader.addressval,
    pedidoHeader.anbdesc,
    pedidoHeader.cod,
    pedidoHeader.num,
    total,
    user.name,
    user.zondesc,
  ]);

  return (
    <Container>
      <Loading isLoading={loading} />
      <ScrollTop />
      <SGOHeader />
      <SGONavbar
        noLinks
        title="Verificação de Saldo no Estoque"
        isResponsible
      />
      <Content>
        {!loading && (
          <>
            <HeaderContent>
              <div>
                <span>
                  <p>
                    Pedido N°:&nbsp;
                    <strong>{pedidoHeader.num}</strong>
                  </p>
                </span>
                <span>
                  <p>
                    Data do Pedido:&nbsp;
                    <strong>{formatDate(pedidoHeader.dtpedido)}</strong>
                  </p>
                </span>
                <span>
                  <p>
                    Coordenação:&nbsp;
                    <strong>{pedidoHeader.anbdesc}</strong>
                  </p>
                </span>
              </div>
              <div>
                <span>
                  <p>
                    Endereço de Entrega:&nbsp;
                    <strong>{pedidoHeader.address.street}</strong>
                  </p>
                </span>
              </div>
            </HeaderContent>

            <Total>
              <p>
                Total do Pedido: <strong>R$ {formatNumber(total)}</strong>
              </p>
            </Total>

            <FaultyDiv style={faultyAppear}>
              <p>
                Há <span>{faulty}</span> ite{faulty > 1 ? 'ns' : 'm'} que requer
                {faulty > 1 ? 'em' : ''} sua atenção.
              </p>
            </FaultyDiv>

            <ButtonFinish style={appear}>
              <button
                type="button"
                onClick={() => setInc((state) => ({ ...state, open: true }))}
              >
                Emitir GD <FaAngleDoubleRight />
              </button>
            </ButtonFinish>
            <Form translate={undefined}
              ref={phaseOne}
              onSubmit={() => {
                return null;
              }}
            >
              <Grid>
                {items.map((item: ItemsProps) => (
                  <GridItem
                    key={item.matcod}
                    $required
                    $color={
                      item.require
                        ? item.selected.length < item.quantity
                          ? '#fda50f'
                          : item.selected.length === item.quantity
                          ? '#267326'
                          : '#c53030'
                        : item.stored < item.quantity
                        ? '#c53030'
                        : '#267326'
                    }
                  >
                    <div>
                      <strong>
                        {item.matcod} - {item.matdesc}
                      </strong>
                    </div>
                    <div>
                      <span>
                        <p>
                          Custo:&nbsp;
                          <strong>R$ {formatNumber(item.cost)}</strong>
                        </p>
                      </span>
                      <span>
                        <p>Quantidade:&nbsp;</p>
                        <strong>{item.quantity}</strong>
                      </span>
                    </div>
                    <div>
                      <span>
                        <p>
                          Seu estoque atual:&nbsp;
                          <strong>{item.stored}</strong>
                        </p>
                      </span>
                    </div>
                    <div>
                      <span>
                        <p>
                          Total:&nbsp;
                          <strong>
                            R$ {calcTotal(item.quantity, item.cost)}
                          </strong>
                        </p>
                      </span>
                    </div>
                    {item.require && (
                      <>
                        <SpanMessage
                          $color={
                            item.selected.length < item.quantity
                              ? '#fda50f'
                              : item.selected.length === item.quantity
                              ? '#267326'
                              : '#c53030'
                          }
                        >
                          <p>
                            {item.selected.length === 0
                              ? 'É necessário selecionar os números controlados'
                              : item.selected.length < item.quantity
                              ? 'Quantidade selecionada é inferior a quantidade apontada'
                              : item.selected.length > item.quantity
                              ? 'Quantidade selecionada excede a quantidade apontada'
                              : ''}
                          </p>
                        </SpanMessage>
                        <div>
                          <span>
                            <DeclareButton as="button"
                              type="button"
                              onClick={() =>
                                setUpdateDiag({
                                  open: true,
                                  content: (
                                    <DeclareNumbers>
                                      {item.numbers.map((number: string) => (
                                        <Checkbox
                                          id={`N${number}`}
                                          title={number}
                                          onClick={() =>
                                            handleDeclareClick(
                                              item.matcod,
                                              number,
                                            )}
                                          checked={[...item.selected].includes(
                                            number,
                                          )}
                                        />
                                      ))}
                                    </DeclareNumbers>
                                  ),
                                  values: {
                                    matcod: item.matcod,
                                    matdesc: item.matdesc,
                                    max: item.quantity,
                                  },
                                })
                              }
                            >
                              Selecionar numeração controlada
                            </DeclareButton>
                          </span>
                        </div>
                      </>
                    )}

                    {item.stored < item.quantity && (
                      <Insufficient>
                        <p>
                          Não há estoque suficiente para atender a solicitação.
                        </p>
                      </Insufficient>
                    )}
                  </GridItem>
                ))}
              </Grid>
            </Form>
          </>
        )}
        <U.Container scroll="paper" maxWidth={false} open={updateDiag.open}>
          <U.Title>
            <h2>{updateDiag.values?.matdesc || 'Material'}</h2>
          </U.Title>
          <U.Content>{updateDiag.content}</U.Content>
          <U.Actions>
            <U.Confirm as="button"
              type="button"
              onClick={() => setUpdateDiag({ open: false })}
            >
              Confirmar
            </U.Confirm>
          </U.Actions>
        </U.Container>
        <I.Container scroll="paper" maxWidth={false} open={inc.open}>
          <I.Title>
            <h2>{inc.title || 'Inclusão'}</h2>
          </I.Title>
          <I.Content>{inc.content}</I.Content>
          <I.Actions>
            <I.Cancel as="button"
              type="button"
              onClick={() =>
                setInc({
                  ...inc,
                  open: false,
                })}
            >
              Cancelar
            </I.Cancel>
            <I.Confirm as="button" type="button" onClick={handleFinish}>
              Finalizar
            </I.Confirm>
          </I.Actions>
        </I.Container>
      </Content>
      <SGOFooter />
    </Container>
  );
};

export default GDPedido;
